/**
 * O bloco de borda a borda onde Finanças e Manutenções desenham a tela da origem.
 *
 * Papel: reproduz o `<iframe>` que o HTML principal esticava por toda a largura
 * (`renderFinancasFamilia` l. 4376, `renderAssuntoBoaVista` l. 2951) e, dentro
 * dele, o começo de cada miolo: a barra com os logotipos, as abas e o respiro do
 * `main`. Fornece aos painéis o tema, a notificação curta e, a cada um, se ele
 * é o da aba à vista (`useAbaVisivel`).
 *
 * Como usar: a página do módulo monta o `CabecalhoDoModulo` e, logo abaixo, o
 * `QuadroEmbutido` com as abas e um painel por aba. O quadro sai da largura do
 * `<main>` com `-mx-8`, a exceção à regra do `layout.tsx` que só ele tem; uma
 * página que pusesse o quadro dentro de outro contêiner com respiro lateral
 * deixaria de ir de borda a borda.
 *
 * Todo painel fica montado o tempo todo, e o inativo recebe `hidden`. É o que a
 * origem faz com `display:none`, e é o que mantém filtro, ordenação e texto
 * digitado de uma aba quando a pessoa vai a outra e volta. Um painel que
 * consultasse a API a cada montagem não sofre com isso: a consulta sai uma vez,
 * quando o quadro monta. A aba ativa não vai para o endereço, como na origem:
 * recarregar a página volta à primeira aba.
 */
import { type ReactNode, useState } from 'react';

import { cn } from '@/shared/lib/utils';
import { type AbaDoApp, AbasDoApp } from './abas-do-app';
import { BarraDoApp } from './barra-do-app';
import { ContextoDaAbaVisivel } from './contexto-da-aba';
import { ProvedorDeNotificacao } from './provedor-de-notificacao';
import { CLASSE_DO_TEMA, ContextoDoTema, type TemaEmbutido } from './tema-embutido';

/**
 * O espaço depois do bloco: 36 px do `.frota-block` em Finanças, os 32 px que a
 * origem de Manutenções põe por `style`.
 */
const MARGEM_POR_TEMA: Readonly<Record<TemaEmbutido, string>> = {
  financeiro: 'mb-9',
  manutencoes: 'mb-8',
};

/** O respiro do `main` de cada origem. */
const RESPIRO_POR_TEMA: Readonly<Record<TemaEmbutido, string>> = {
  financeiro: 'px-5 py-[18px]',
  manutencoes: 'px-7 pt-[22px] pb-[60px]',
};

interface QuadroEmbutidoProps {
  tema: TemaEmbutido;
  /** As abas, na ordem da origem; a primeira abre. */
  abas: readonly AbaDoApp[];
  /** O conteúdo de cada aba, pela chave dela. */
  paineis: Readonly<Record<string, ReactNode>>;
  className?: string;
}

/**
 * O quadro de um miolo.
 *
 * @param tema - Qual origem o quadro reproduz.
 * @param abas - As abas do miolo.
 * @param paineis - O conteúdo de cada aba, por chave.
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O bloco com barra, abas e painéis.
 */
export function QuadroEmbutido({ tema, abas, paineis, className }: QuadroEmbutidoProps) {
  const [ativa, setAtiva] = useState(() => abas[0]?.chave ?? '');

  return (
    <ContextoDoTema value={tema}>
      <ProvedorDeNotificacao>
        <section
          className={cn(
            CLASSE_DO_TEMA[tema],
            '-mx-8 flow-root overflow-x-clip bg-background',
            MARGEM_POR_TEMA[tema],
            className,
          )}
        >
          <BarraDoApp tema={tema} />
          <AbasDoApp tema={tema} abas={abas} ativa={ativa} onTrocar={setAtiva} />
          {abas.map(({ chave }) => (
            <div
              key={chave}
              role="tabpanel"
              id={`painel-${chave}`}
              aria-labelledby={`aba-${chave}`}
              hidden={chave !== ativa}
              className={RESPIRO_POR_TEMA[tema]}
            >
              <ContextoDaAbaVisivel value={chave === ativa}>
                {paineis[chave]}
              </ContextoDaAbaVisivel>
            </div>
          ))}
        </section>
      </ProvedorDeNotificacao>
    </ContextoDoTema>
  );
}
